import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import * as RpcResponse from 'ox/RpcResponse'
import * as React from 'react'
import { Button, Frame, Input } from 'regen-ui'
import * as z from 'zod/mini'

import * as Auth from '#/lib/auth.js'
import { useAnalytics } from '#/lib/telemetry/analytics.js'
import * as RequestFrames from '#/routes/remote/-frames/request/index.js'
import * as DeviceAuth from '#/routes/remote/-lib/DeviceAuth.js'
import * as RemoteRpc from '#/routes/remote/-lib/RemoteRpc.js'

/** Standalone entry page for the Wata device-code RPC flow. */
export const Route = createFileRoute('/remote/auth/device')({
  component: RouteComponent,
  validateSearch: z.object({
    name: z.optional(z.string()),
    status: z.optional(z.union([z.literal('approved'), z.literal('error'), z.literal('rejected')])),
    user_code: z.optional(z.string()),
  }),
})

function RouteComponent() {
  const { capture } = useAnalytics()
  const sessionAddress = Auth.useSessionAddress()
  const rpc = RemoteRpc.useOptional()
  const router = useRouter()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { status, user_code } = search
  const userCode = React.useMemo(
    () => (user_code ? DeviceAuth.normalizeUserCode(user_code) : ''),
    [user_code],
  )
  const name = rpc?.consumer.name ?? rpc?.consumer.host ?? search.name ?? 'the requesting app'

  React.useEffect(() => {
    capture('cli_auth_flow_started', { transport: 'device-code', authed: !!sessionAddress })
  }, [capture, sessionAddress])

  const pending = useQuery({
    enabled: !!userCode && !status,
    queryKey: ['device-auth', 'pending', userCode],
    queryFn: () => DeviceAuth.fetchPending(userCode),
    // A code registered moments ago may not be visible to this colo yet.
    retry: 2,
    retryDelay: 1_000,
  })

  const reject = useMutation({
    mutationFn: () => DeviceAuth.deny({ userCode }),
    onSuccess: () =>
      void navigate({
        replace: true,
        search: { name: pending.data?.meta?.name, status: 'rejected' },
        to: '/remote/auth/device',
      }),
  })

  React.useEffect(() => {
    if (!pending.data || status) return
    const request = DeviceAuth.toRequest(pending.data)
    if (!request) return
    const rpc = DeviceAuth.toRemote(pending.data, {
      onComplete: (status) => {
        void navigate({
          replace: true,
          search: { name: pending.data.meta?.name, status },
          to: '/remote/auth/device',
        })
      },
      onError: () => {
        void navigate({
          replace: true,
          search: { name: pending.data.meta?.name, status: 'error' },
          to: '/remote/auth/device',
        })
      },
      userCode,
    })
    if (!rpc) return

    let active = true
    void RemoteRpc.applyContext(request.context)
      .then(() => {
        if (active) RemoteRpc.open(router, rpc, { replace: true, search: { mode: 'standalone' } })
      })
      .catch((error) => {
        if (active) void rpc.reject(RpcResponse.parseError(error))
      })
    return () => {
      active = false
    }
  }, [navigate, pending.data, router, status, userCode])

  if (status === 'approved')
    return (
      <RequestFrames.Result
        description={
          <span className="text-label-13">
            You can return to <span className="text-foreground">{name}</span>
          </span>
        }
        title="Request approved"
        variant="success"
      />
    )

  if (status === 'rejected')
    return (
      <RequestFrames.Result
        description={
          <>
            No wallet action was completed.
            <br />
            <span className="text-label-13">
              Return to <span className="text-foreground">{name}</span> for details.
            </span>
          </>
        }
        title="Request rejected"
        variant="error"
      />
    )

  if (status === 'error')
    return (
      <RequestFrames.Result
        description={
          <>
            The request failed.
            <br />
            <span className="text-label-13">
              Return to <span className="text-foreground">{name}</span> for details.
            </span>
          </>
        }
        title="Request failed"
        variant="error"
      />
    )

  if (!userCode)
    return (
      <Frame
        subtitle="Paste the device code shown in the requesting app to continue."
        title="Authorize request"
      >
        <CodeForm />
      </Frame>
    )

  if (pending.isPending) return <span data-route-pending hidden />

  if (pending.isError || !pending.data)
    return (
      <Frame subtitle="We couldn't load that device code." title="Authorize request">
        <p className="text-copy-14 text-negative">
          {pending.error instanceof Error ? pending.error.message : 'Invalid device code.'}
        </p>
        <CodeForm />
      </Frame>
    )

  if (!DeviceAuth.toRequest(pending.data))
    return (
      <Frame subtitle="The request envelope is not supported." title="Authorize request">
        <Button loading={reject.isPending} onClick={() => reject.mutate()} variant="secondary">
          Deny request
        </Button>
        {reject.error && <p className="text-copy-14 text-negative">{reject.error.message}</p>}
      </Frame>
    )

  return <span data-route-pending hidden />
}

function CodeForm() {
  const navigate = Route.useNavigate()

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault()
        const form = new FormData(event.currentTarget)
        const value = String(form.get('user_code') ?? '')
        void navigate({ search: { user_code: DeviceAuth.normalizeUserCode(value) } })
      }}
    >
      <Input autoFocus label="Device code" name="user_code" placeholder="BCDF-GHJK…" />
      <Button type="submit" variant="primary">
        Load request
      </Button>
    </form>
  )
}
